import { Activity } from 'lucide-react';
import { useRecentMembershipActivity } from '../../queries';

const NULL_SYMBOL = '—';

const formatRelative = (iso: string | null | undefined): string => {
  if (!iso) return NULL_SYMBOL;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return NULL_SYMBOL;
  const minutes = Math.floor((Date.now() - parsed.getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  if (hours < 48) return 'Yesterday';
  return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const initials = (name: string | null | undefined): string => {
  const safe = String(name || '').trim();
  if (!safe) return '?';
  const parts = safe.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || safe[0]!.toUpperCase();
};

const formatAction = (action: string | null | undefined): { label: string; tone: 'earn' | 'redeem' | 'adjust' | 'other' } => {
  const value = String(action || '').toUpperCase();
  if (value.includes('REDEEM')) return { label: 'REDEEM', tone: 'redeem' };
  if (value.includes('EARN')) return { label: 'EARN', tone: 'earn' };
  if (value.includes('ADJUST')) return { label: 'ADJUST', tone: 'adjust' };
  if (value.includes('ISSUE')) return { label: 'ISSUE', tone: 'other' };
  return { label: value || 'EVENT', tone: 'other' };
};

const RecentActivityPanel = () => {
  const query = useRecentMembershipActivity(true);
  const rows = query.data ?? [];

  return (
    <article className="actions-panel" data-testid="recent-activity-panel">
      <header className="actions-panel__header">
        <span className="actions-panel__icon" aria-hidden="true">
          <Activity size={16} />
        </span>
        <div className="actions-panel__title-block">
          <h3 className="actions-panel__title">Recent activity</h3>
        </div>
        <span className="actions-panel__suffix">Last <strong>24h</strong></span>
      </header>

      {query.isLoading ? (
        <div className="actions-panel__loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="actions-panel__empty">No activity in the last 24 hours.</div>
      ) : (
        <ul className="actions-panel__list">
          {rows.map((row, index) => {
            const { label, tone } = formatAction(row.action);
            return (
              <li
                key={`${row.membershipUuid || 'm'}-${index}`}
                className="actions-panel__item"
                data-testid={`recent-activity-${index}`}
              >
                <span className="actions-panel__avatar" aria-hidden="true">
                  {initials(row.customerName)}
                </span>
                <div className="actions-panel__item-body">
                  <div className="actions-panel__item-line">
                    <span className="actions-panel__item-name">{row.customerName || NULL_SYMBOL}</span>
                  </div>
                  <div className="actions-panel__item-sub">{row.tierName || NULL_SYMBOL}</div>
                </div>
                <span className={`actions-panel__chip actions-panel__chip--${tone}`}>{label}</span>
                <span className="actions-panel__time">{formatRelative(row.createdAt)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </article>
  );
};

export default RecentActivityPanel;
